import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { withSelectedIds } from "@/utils/selectionStats";
import { buildApiFilterParams } from "@/utils/apiFilterParams";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: 'name', stock_tracking: true };

const PRODUCT_ORDERING_TO_STOCKPRODUCT = {
    name: 'product_name',
    '-name': '-product_name',
    upc: 'product_upc',
    '-upc': '-product_upc',
    manufacturer: 'product_manufacturer',
    '-manufacturer': '-product_manufacturer',
    stock_count: 'count',
    '-stock_count': '-count',
    customer_name: 'product_customer_name',
    '-customer_name': '-product_customer_name',
};

function mapProductOrderingToStockProduct(ordering) {
    if (!ordering) {
        return ordering;
    }
    return PRODUCT_ORDERING_TO_STOCKPRODUCT[ordering] || ordering;
}

/** Query params for the stock page list/stats/CSV against /api/stockproducts/. */
function buildStockProductsParams(filters, { includePagination = false } = {}) {
    const base = buildApiFilterParams(filters, { stripPagination: !includePagination });
    const params = { ...base };

    // buildApiFilterParams remaps legacy `format` → sale_format (catalog).
    // Stockproducts filters by product format.
    if (
        params.sale_format != null
        && params.sale_format !== ""
        && (params.product_format == null || params.product_format === "")
    ) {
        params.product_format = params.sale_format;
    }
    delete params.sale_format;

    // Product-list-only filters that stockproducts does not implement.
    delete params.stock_tracking;
    delete params.owned;
    delete params.catalog;
    delete params.available_for_sale;
    delete params.missing_purchase_price;
    delete params.lowstock;

    if (params.ordering) {
        params.ordering = mapProductOrderingToStockProduct(params.ordering);
    }

    // Never-stocked virtual rows only exist for operator customer + stock scope.
    if (params.with_stock) {
        delete params.include_never_stocked;
    } else if (params.customer && params.stock) {
        params.include_never_stocked = true;
    } else {
        delete params.include_never_stocked;
    }

    return params;
}

/** Rows are products; stats filter on the underlying stock product line. */
export function getSelectedStockProductId(row) {
    return row?.stockproduct;
}

function mapStockProductToProductRow(sp) {
    const product = { ...(sp.product_data || {}) };
    const ownerData = sp.owner_data || {};
    product.stock_count = sp.count;
    product.stock_location = sp.location;
    product.stockproduct = sp.id;
    product.stock_data = sp.stock_data || null;
    product.stock_name = $utils.getStockName(sp.stock_data);
    product.stockproduct_data = {
        id: sp.id,
        archived: sp.archived,
        count: sp.count,
        location_balances: sp.location_balances || [],
        storage_locations_enabled: !!sp.stock_data?.storage_locations_enabled,
        reserved: sp.reserved,
        reserved_for_orders: sp.reserved_for_orders,
        stock: sp.stock,
        owner: sp.owner,
        owner_data: sp.owner_data,
        location: sp.location,
        owned: true,
    };
    product.customer_name =
        ownerData.name || product.owner_data?.name || product.customer_name;
    product._stockproduct_row_id =
        sp.id != null ? `sp-${sp.id}` : `virtual-${product.id}-${sp.owner}`;
    return product;
}

function canViewStockPage(rootGetters) {
    const permissions = rootGetters['session/current_user_permissions'] || [];
    return (
        permissions.indexOf('core.view_product') !== -1
        || permissions.indexOf('stock.view_stockproduct') !== -1
    );
}

const state = {
    stock: null,
    products: [],
    products_count: 0,
    products_filters: DEFAULT_FILTERS,
    products_loading: false,
    products_list_loaded: false,
    products_loading_errors: null,
    products_cancel_source: null,

    products_stats: {},
    products_stats_loading: false,
    products_stats_loading_errors: null,
    products_stats_cancel_source: null,
    selected_items: [],
    bulk_action_loading: false,
};

const mutations = {
    updateProducts(state, products) {
        state.products = products;
    },
    updateProductsFilters(state, products_filters) {
        const prev = state.products_filters || {};
        const next = { ...products_filters };
        if (prev.stock !== next.stock) {
            delete next.stock_owner;
        }
        state.products_filters = next;
        if (state.stock?.id != next.stock) {
            // Reset until the matching stock object is fetched (fetchStock).
            state.stock = null;
        }
        // Save filters to localStorage
        saveFiltersToStorage(getStorageKey('stock', 'default'), state.products_filters);
    },
    updateProductsCount(state, products_count) {
        state.products_count = products_count;
    },
    updateProductsLoading(state, loading) {
        state.products_loading = loading;
    },
    updateProductsListLoaded(state, loaded) {
        state.products_list_loaded = loaded;
    },
    updateProductsLoadingErrors(state, errors) {
        state.products_loading_errors = errors;
    },
    updateProductsCancelSource(state, source) {
        state.products_cancel_source = source;
    },
    updateProductsStats(state, products_stats) {
        state.products_stats = products_stats;
    },
    updateProductsStatsLoading(state, loading) {
        state.products_stats_loading = loading;
    },
    updateProductsStatsLoadingErrors(state, errors) {
        state.products_stats_loading_errors = errors;
    },
    updateProductsStatsCancelSource(state, source) {
        state.products_stats_cancel_source = source;
    },
    updateStock(state, stock) {
        state.stock = stock;
    },
    updateSelectedItems(state, items) {
        state.selected_items = items;
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },
};

const actions = {

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/products/bulk/",
                action: params.action,
                instances: state.selected_items.map((item) => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            dispatch('fetchProducts');
            dispatch("session/fetchStats", null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },
    async fetchStock({ commit, dispatch, state }, params) {
        if (!params.stock) {
            commit('updateStock', null);
            return;
        }

        let url = `/api/stocks/${params.stock}/`;
        try {
            const response = await axios.get(url);
            commit('updateStock', response.data);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        }
    },

    exportProductsAsCSV({ state }, params) {
        const filters = params.filtered
            ? buildStockProductsParams(state.products_filters, { includePagination: true })
            : {};
        delete filters.offset;
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;

        $utils.downloadFile({
            url: "/api/stockproducts/",
            params: filters,
        }, $utils.getFilename('stock'));
    },

    async fetchProducts({ commit, state, dispatch, rootGetters }, options = {}) {
        if (!canViewStockPage(rootGetters)) {
            return;
        }
        commit('updateProductsLoading', true);
        commit('updateProductsLoadingErrors', null);

        if (state.products_cancel_source) {
            state.products_cancel_source.cancel("canceled");
        }
        const filters = options.filters
            ? { ...options.filters }
            : loadFiltersFromStorage(getStorageKey('stock', 'default'), DEFAULT_FILTERS);
        commit('updateProductsFilters', filters);

        // Keep the stock object in sync with the stock filter: columns such
        // as "stock tracking" depend on it, including on first page load.
        if (state.stock?.id != filters.stock) {
            dispatch('fetchStock', { stock: filters.stock }).catch(() => {});
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateProductsCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/stockproducts/', {
                params: buildStockProductsParams(filters, { includePagination: true }),
                cancelToken: state.products_cancel_source?.token,
            });

            commit(
                'updateProducts',
                (response.data.results || []).map(mapStockProductToProductRow),
            );
            commit('updateProductsCount', response.data.count);
            commit('updateProductsCancelSource', null);
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateProductsLoadingErrors', error.details);
            commit('updateProductsCancelSource', null);
            throw error;
        } finally {
            commit('updateProductsLoading', false);
            commit('updateProductsListLoaded', true);
            dispatch('fetchProductsStats');
        }
    },

    async fetchProductsStats({ commit, state }) {
        commit('updateProductsStatsLoading', true);
        commit('updateProductsStatsLoadingErrors', null);

        if (state.products_stats_cancel_source) {
            state.products_stats_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateProductsStatsCancelSource', cancelSource);

        try {
            const params = buildStockProductsParams(state.products_filters);
            const response = await axios.get('/api/stockproducts/stats/', {
                params: withSelectedIds(
                    params,
                    state.selected_items,
                    getSelectedStockProductId,
                ),
                cancelToken: state.products_stats_cancel_source?.token,
            });
            commit('updateProductsStats', {
                products: response.data.products,
                stock_count: response.data.total_quantity,
                stock_value: response.data.total_value,
            });
            commit('updateProductsStatsCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateProductsStatsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateProductsStatsLoading', false);
        }
    },

    async changeProductsFilters({ commit }, filters) {
        // fetchProducts (called right after by the listing) takes care of
        // fetching the stock object matching the new filter.
        commit('updateProductsFilters', filters);
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
